import React from "react";
import {
  ALL_PARTNER_EXPORT_FIELD_KEYS,
  DEFAULT_PARTNER_EXPORT_FIELDS,
  PARTNER_EXPORT_FIELD_GROUPS,
  PARTNER_EXPORT_STATUS_OPTIONS,
  PartnerExportFieldKey,
  PartnerExportStatusSlug,
} from "../../constants/partnerExportFields.ts";
import { exportPartnersExcel } from "../../services/partner.service.ts";
import { notification } from "../../utils/Notifications.tsx";

interface ExportColaboradoresModalProps {
  query: string;
  sortBy: string;
  defaultSort: string;
  menuSelected: string;
  closeFn: () => void;
}

const STATUS_COLORS: Record<
  (typeof PARTNER_EXPORT_STATUS_OPTIONS)[number]["value"],
  { bg: string; border: string; text: string }
> = {
  activo: { bg: "#ecfdf5", border: "#6ee7b7", text: "#047857" },
  baja: { bg: "#f9fafb", border: "#d1d5db", text: "#374151" },
  suspendido: { bg: "#fff7ed", border: "#fdba74", text: "#c2410c" },
  "pendiente-baja": { bg: "#fefce8", border: "#fde047", text: "#a16207" },
};

const columnActionBtn =
  (variant: "default" | "clear" = "default"): React.CSSProperties => ({
    background: "#fff",
    border: `1px solid ${variant === "clear" ? "#e5e7eb" : "#cbd5e1"}`,
    color: variant === "clear" ? "#64748b" : "#1e293b",
    borderRadius: "8px",
    padding: "6px 14px",
    fontSize: "13px",
    fontWeight: 500,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
  });

const sectionLabel: React.CSSProperties = {
  fontSize: "12px",
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#6b7280",
  marginBottom: "10px",
};

function parseColaboradoresQuery(query: string): Record<string, string | number> {
  const filters: Record<string, string | number> = {};
  if (!query) return filters;
  const params = new URLSearchParams(query);
  params.forEach((value, key) => {
    if (
      ["branch", "puesto", "shift", "salario", "transportMethod", "sucursal"].includes(
        key
      )
    ) {
      const parsed = Number(value);
      filters[key] = Number.isNaN(parsed) ? value : parsed;
      return;
    }
    filters[key] = value;
  });
  return filters;
}

function buildExportFilters(
  query: string,
  sortBy: string,
  defaultSort: string,
  menuSelected: string,
  exportStatuses: PartnerExportStatusSlug[]
) {
  const parsed = parseColaboradoresQuery(query);
  const tab = parsed.tab ?? menuSelected;
  return {
    ...parsed,
    tab:
      exportStatuses.length > 0
        ? "todos"
        : typeof tab === "string"
          ? tab.toLowerCase()
          : tab,
    sort: sortBy,
    order: defaultSort,
    exportStatuses,
  };
}

function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

const ExportColaboradoresModal: React.FC<ExportColaboradoresModalProps> = ({
  query,
  sortBy,
  defaultSort,
  menuSelected,
  closeFn,
}) => {
  const [selectedFields, setSelectedFields] = React.useState<
    PartnerExportFieldKey[]
  >([...DEFAULT_PARTNER_EXPORT_FIELDS]);
  const [selectedStatuses, setSelectedStatuses] = React.useState<
    PartnerExportStatusSlug[]
  >(["activo"]);
  const [activeGroupTitle, setActiveGroupTitle] = React.useState(
    PARTNER_EXPORT_FIELD_GROUPS[0].title
  );
  const [loading, setLoading] = React.useState(false);

  const toggleField = (field: PartnerExportFieldKey) => {
    setSelectedFields((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
    );
  };

  const toggleStatus = (status: PartnerExportStatusSlug) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const toggleGroupFields = (groupIndex: number, select: boolean) => {
    const keys = PARTNER_EXPORT_FIELD_GROUPS[groupIndex].fields.map((f) => f.key);
    setSelectedFields((prev) => {
      if (select) return [...new Set([...prev, ...keys])];
      return prev.filter((k) => !keys.includes(k));
    });
  };

  const visibleGroup =
    PARTNER_EXPORT_FIELD_GROUPS.find((g) => g.title === activeGroupTitle) ??
    PARTNER_EXPORT_FIELD_GROUPS[0];

  const handleExport = async () => {
    if (!selectedFields.length) {
      notification("Faltan columnas", "Elige al menos una columna.", "warning");
      return;
    }
    if (!selectedStatuses.length) {
      notification(
        "Faltan colaboradores",
        "Elige al menos un estatus laboral.",
        "warning"
      );
      return;
    }

    setLoading(true);
    try {
      const filters = buildExportFilters(
        query,
        sortBy,
        defaultSort,
        menuSelected,
        selectedStatuses
      );
      const response = await exportPartnersExcel({ fields: selectedFields, ...filters });
      downloadBlob(
        response.data,
        `colaboradores_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
      notification("Listo", "Excel descargado.", "success");
      closeFn();
    } catch {
      notification("Error", "No se pudo descargar el Excel.", "danger");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>
        {`
          .export-col-action-btn:hover:not(:disabled) {
            background: #f8fafc !important;
            border-color: #94a3b8 !important;
          }
          .export-col-action-btn--clear:hover:not(:disabled) {
            background: #fef2f2 !important;
            border-color: #fca5a5 !important;
            color: #b91c1c !important;
          }
          .export-col-action-btn:disabled {
            opacity: 0.55;
            cursor: not-allowed;
          }
        `}
      </style>
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* Colaboradores */}
      <div>
        <div className="mb-2">
          <div style={{ ...sectionLabel, marginBottom: 0 }}>Colaboradores</div>
        </div>

        <div className="d-flex gap-2 w-100">
          {PARTNER_EXPORT_STATUS_OPTIONS.map((status) => {
            const selected = selectedStatuses.includes(status.value);
            const colors = STATUS_COLORS[status.value];
            return (
              <label
                key={status.value}
                title={status.description}
                className="d-flex align-items-center gap-2 mb-0"
                style={{
                  flex: "1 1 0",
                  minWidth: 0,
                  border: `1.5px solid ${selected ? colors.border : "#e5e7eb"}`,
                  background: selected ? colors.bg : "#fff",
                  color: selected ? colors.text : "#374151",
                  borderRadius: "10px",
                  padding: "10px 12px",
                  fontSize: "13px",
                  fontWeight: selected ? 600 : 500,
                  cursor: loading ? "default" : "pointer",
                  transition: "all 0.15s ease",
                }}
              >
                <input
                  type="checkbox"
                  className="form-check-input flex-shrink-0 mt-0"
                  checked={selected}
                  onChange={() => toggleStatus(status.value)}
                  disabled={loading}
                />
                <span className="text-truncate">{status.label}</span>
              </label>
            );
          })}
        </div>
      </div>

      <hr className="my-0" style={{ borderColor: "#e5e7eb" }} />

      {/* Columnas */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div style={{ ...sectionLabel, marginBottom: 0 }}>Columnas</div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <button
              type="button"
              className="export-col-action-btn"
              style={columnActionBtn()}
              onClick={() => setSelectedFields([...DEFAULT_PARTNER_EXPORT_FIELDS])}
              disabled={loading}
            >
              Seleccionar recomendadas
            </button>
            <button
              type="button"
              className="export-col-action-btn"
              style={columnActionBtn()}
              onClick={() => setSelectedFields([...ALL_PARTNER_EXPORT_FIELD_KEYS])}
              disabled={loading}
            >
              Seleccionar todas
            </button>
            <button
              type="button"
              className="export-col-action-btn export-col-action-btn--clear"
              style={columnActionBtn("clear")}
              onClick={() => setSelectedFields([])}
              disabled={loading}
            >
              Limpiar todo
            </button>
          </div>
        </div>

        <div
          className="d-flex border rounded overflow-hidden"
          style={{ height: 320, background: "#fafafa" }}
        >
          <div
            className="border-end bg-white"
            style={{ width: 170, flexShrink: 0, overflowY: "auto" }}
          >
            {PARTNER_EXPORT_FIELD_GROUPS.map((group) => {
              const groupKeys = group.fields.map((f) => f.key);
              const selectedCount = groupKeys.filter((k) =>
                selectedFields.includes(k)
              ).length;
              const isActive = visibleGroup?.title === group.title;

              return (
                <button
                  key={group.title}
                  type="button"
                  disabled={loading}
                  onClick={() => setActiveGroupTitle(group.title)}
                  style={{
                    display: "block",
                    width: "100%",
                    border: "none",
                    borderLeft: isActive ? "3px solid #2563eb" : "3px solid transparent",
                    background: isActive ? "#eff6ff" : "transparent",
                    padding: "9px 12px",
                    textAlign: "left",
                    fontSize: "13px",
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? "#1d4ed8" : "#374151",
                    cursor: "pointer",
                  }}
                >
                  {group.title}
                  {selectedCount > 0 && (
                    <span
                      className="ms-1"
                      style={{
                        fontSize: "11px",
                        background: isActive ? "#dbeafe" : "#f3f4f6",
                        color: isActive ? "#1d4ed8" : "#6b7280",
                        padding: "1px 6px",
                        borderRadius: 10,
                      }}
                    >
                      {selectedCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex-grow-1 p-3 overflow-auto bg-white">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span style={{ fontWeight: 600, fontSize: "14px" }}>
                {visibleGroup.title}
              </span>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0"
                  onClick={() => {
                    const idx = PARTNER_EXPORT_FIELD_GROUPS.findIndex(
                      (g) => g.title === visibleGroup.title
                    );
                    if (idx >= 0) toggleGroupFields(idx, true);
                  }}
                  disabled={loading}
                >
                  Marcar grupo
                </button>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-muted"
                  onClick={() => {
                    const idx = PARTNER_EXPORT_FIELD_GROUPS.findIndex(
                      (g) => g.title === visibleGroup.title
                    );
                    if (idx >= 0) toggleGroupFields(idx, false);
                  }}
                  disabled={loading}
                >
                  Limpiar
                </button>
              </div>
            </div>

            <div className="d-flex flex-column gap-1">
              {visibleGroup.fields.map((field) => {
                const checked = selectedFields.includes(field.key);
                return (
                  <button
                    key={field.key}
                    type="button"
                    disabled={loading}
                    onClick={() => toggleField(field.key)}
                    className="d-flex align-items-start gap-2 text-start w-100"
                    style={{
                      border: "none",
                      background: checked ? "#f0f9ff" : "transparent",
                      borderRadius: 8,
                      padding: "7px 10px",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      className="form-check-input mt-1"
                      checked={checked}
                      readOnly
                      tabIndex={-1}
                    />
                    <span>
                      <span className="d-block" style={{ fontSize: "14px" }}>
                        {field.label}
                      </span>
                      {field.hint && (
                        <small className="text-muted">{field.hint}</small>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="d-flex justify-content-between align-items-center pt-2 border-top">
        <span className="text-muted" style={{ fontSize: "13px" }}>
          {selectedStatuses.length} estatus · {selectedFields.length} columnas
        </span>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-light"
            onClick={closeFn}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-azul"
            onClick={handleExport}
            disabled={loading}
          >
            {loading ? "Descargando..." : "Descargar Excel"}
          </button>
        </div>
      </div>
      </div>
    </>
  );
};

export default ExportColaboradoresModal;
